import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getRequestAsync, postRequestAsync, putRequestAsync } from '../../../../helpers/apiHandler';
import useFBAuthenticated from '../../../../hooks/useFBAuthenticated';
import useSSE from '../../../../hooks/useSSE';
import WhatsAppLeftPanel from '../navigation/WhatsAppLeftPanel';
import WhatsAppChatPanel from './WhatsAppChatPanel';

const WhatsAppInboxTabContent = ({ accessToken, wabaId, phoneNumberId }) => {
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [userRole, setUserRole] = useState(null);
  const [selectedConversationStatus, setSelectedConversationStatus] = useState('active');

  // Get user info from Redux store
  const user = useSelector((state) => state.auth.user);
  const { isAuthenticated } = useFBAuthenticated(accessToken);

  // SSE connection for real-time updates
  const [sseConnectionStatus, setSseConnectionStatus] = useState('disconnected');

  // SSE handlers
  const handleNewMessage = useCallback((messageData) => {
    // If this message belongs to the currently selected conversation, add it to chatMessages
    if (selectedConversationId && messageData.conversation_id === selectedConversationId) {
      setChatMessages(prevMessages => {
        // Check for duplicates using both message_id and id
        const isDuplicate = prevMessages.some(msg =>
          msg.message_id === messageData.message_id ||
          msg.id === messageData.message_id
        );
        if (isDuplicate) {
          return prevMessages;
        }

        // Transform SSE message to UI format
        const uiMessage = {
          id: messageData.message_id,
          message_id: messageData.message_id,
          conversation_id: messageData.conversation_id,
          direction: messageData.direction,
          message_type: messageData.message_type,
          text: messageData.message_text || messageData.text || '',
          sent_at: messageData.sent_at,
          status: messageData.status
        };

        // Add new message and sort by timestamp to maintain chronological order
        const newMessages = [...prevMessages, uiMessage];

        // Sort messages by sent_at timestamp (oldest first)
        newMessages.sort((a, b) => {
          const timeA = new Date(a.sent_at).getTime();
          const timeB = new Date(b.sent_at).getTime();
          return timeA - timeB;
        });

        return newMessages;
      });
    }

    // Update conversations list to reflect new message
    setConversations(prevConversations => {
      return prevConversations.map(conv => {
        if (conv.id === messageData.conversation_id) {
          // Only mark as unread if:
          // 1. The conversation is not currently selected, AND
          // 2. The message is incoming (not sent by us)
          const shouldMarkUnread = conv.id !== selectedConversationId && messageData.direction === 'incoming';

          // Ensure lastMessage is not empty
          const lastMessageText = messageData.message_text || messageData.text || 'New message';

          return {
            ...conv,
            lastMessage: lastMessageText,
            timestamp: messageData.sent_at,
            isUnread: shouldMarkUnread,
            unreadCount: shouldMarkUnread ? (conv.unreadCount || 0) + 1 : 0,
            messageType: messageData.direction === 'incoming' ? 'incoming' : 'outgoing'
          };
        }
        return conv;
      });
    });
  }, [selectedConversationId]);

  const handleStatusUpdate = useCallback((statusData) => {
    console.log('SSE: Status update received', statusData);

    // Update message status in chatMessages
    setChatMessages(prevMessages => {
      return prevMessages.map(msg => {
        if (msg.message_id === statusData.message_id) {
          return {
            ...msg,
            status: statusData.status
          };
        }
        return msg;
      });
    });
  }, []);

  const handleAssignmentUpdate = useCallback((assignmentData) => {
    console.log('SSE: Assignment update received', assignmentData);

    // Update conversation assignment in conversations list
    setConversations(prevConversations => {
      return prevConversations.map(conv => {
        if (conv.id === assignmentData.conversation_id) {
          return {
            ...conv,
            status: assignmentData.agent_id ? 'assigned' : 'unassigned',
            assignedAgent: assignmentData.agent_id ? {
              id: assignmentData.agent_id,
              name: assignmentData.agent_name || 'Agent',
              email: assignmentData.agent_email || ''
            } : null
          };
        }
        return conv;
      });
    });
  }, []);

  const handleConnectionChange = useCallback((status, data) => {
    console.log('SSE: Connection status changed', status, data);
    setSseConnectionStatus(status);
  }, []);

  // Initialize SSE connection
  const { isConnected: sseIsConnected } = useSSE({
    onNewMessage: handleNewMessage,
    onStatusUpdate: handleStatusUpdate,
    onAssignmentUpdate: handleAssignmentUpdate,
    onConnectionChange: handleConnectionChange
  });

  const loadConversations = useCallback(async () => {
    if (!isAuthenticated || !accessToken?.token || !userRole || !wabaId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const endpoint = userRole === 'manager'
        ? '/v2/whatsapp/manager/conversations'
        : '/v2/whatsapp/agent/conversations';

      const response = await getRequestAsync(endpoint, {
        headers: {
          Authorization: accessToken.token
        },
        params: {
          waba_id: wabaId,
          ...filterOptions
        }
      });

      if (response.success) {
        const conversations = response.data?.conversations || [];
        const formattedConversations = conversations.map(conv => {
          // Prioritize contact display name if it's a saved contact
          const contactName = (conv.isContact && conv.contactData?.displayName)
            ? conv.contactData.displayName
            : (conv.contact?.name || conv.contact_name || conv.recipient_phone || 'Unknown');

          return {
            id: conv.id,
            contact: {
              name: contactName,
              avatar: conv.contact?.avatar || null,
              phone: conv.contact?.phone || conv.recipient_phone || conv.phone_number
            },
            lastMessage: conv.lastMessage || conv.last_message_text || 'New conversation',
            timestamp: conv.last_message_at || conv.last_message_time,
            isUnread: conv.isUnread,
            unreadCount: conv.unreadCount,
            messageType: conv.messageType,
            status: conv.status || conv.assignment_status,
            conversationStatus: conv.conversation_status || conv.conversationStatus || 'active', // Actual conversation open/close status
            isContact: conv.isContact || false,
            contactId: conv.contactId || null,
            contactData: conv.contactData || null,
            assignedAgent: conv.assignedAgent || null
          };
        });
        setConversations(formattedConversations);
      } else {
        setError(response.message || 'Failed to load conversations');
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, accessToken?.token, userRole, wabaId, filterOptions]);

  const loadConversationMessages = useCallback(async (conversationId) => {
    if (!conversationId || !isAuthenticated || !accessToken?.token || !userRole) return;

    setIsLoading(true);
    setError(null);

    try {
      const endpoint = userRole === 'manager'
        ? `/v2/whatsapp/manager/conversations/${conversationId}/messages`
        : `/v2/whatsapp/agent/conversations/${conversationId}/messages`;

      const requestConfig = {
        headers: {
          Authorization: accessToken.token
        }
      };

      // Add waba_id parameter for manager requests to simplify access control
      if (userRole === 'manager' && wabaId) {
        requestConfig.params = {
          waba_id: wabaId
        };
      }

      const response = await getRequestAsync(endpoint, requestConfig);

      if (response.success) {
        const messages = response.data?.messages || response.data || [];
        setChatMessages(messages);
      } else {
        setError(response.message || 'Failed to load conversation messages');
      }
    } catch (err) {
      console.error('Error loading conversation messages:', err);
      setError('Failed to load conversation messages');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, accessToken?.token, userRole, wabaId]);

  const markConversationAsRead = useCallback(async (conversationId) => {
    if (!conversationId || !isAuthenticated || !accessToken?.token || !userRole) return;

    // Check if conversation is already read
    const conversation = conversations.find(conv => conv.id === conversationId);
    if (!conversation || (!conversation.isUnread && conversation.unreadCount === 0)) {
      return; // Already read, no need to make API call
    }

    try {
      // Update local state immediately for better UX
      setConversations(prevConversations => {
        return prevConversations.map(conv => {
          if (conv.id === conversationId) {
            return {
              ...conv,
              isUnread: false,
              unreadCount: 0
            };
          }
          return conv;
        });
      });

      // Make API call to mark conversation as read on backend
      const endpoint = userRole === 'manager'
        ? `/v2/whatsapp/manager/conversations/${conversationId}/mark-read`
        : `/v2/whatsapp/agent/conversations/${conversationId}/mark-read`;

      const requestConfig = {
        headers: {
          Authorization: accessToken.token
        }
      };

      // Add waba_id parameter for manager requests
      if (userRole === 'manager' && wabaId) {
        requestConfig.params = {
          waba_id: wabaId
        };
      }

      const response = await putRequestAsync(endpoint, {}, accessToken.token);

      if (response.success) {
        // Reload conversations to sync with backend state
        await loadConversations();
      } else {
        console.error('Failed to mark conversation as read:', response.message);
      }
    } catch (err) {
      console.error('Error marking conversation as read:', err);
      // Reload conversations to restore correct state
      await loadConversations();
    }
  }, [conversations, isAuthenticated, accessToken?.token, userRole, wabaId, loadConversations]);

  const handleConversationClick = useCallback(async (conversationId) => {
    setSelectedConversationId(conversationId);

    // Find the selected conversation and set its status
    const selectedConv = conversations.find(conv => conv.id === conversationId);
    if (selectedConv) {
      setSelectedConversationStatus(selectedConv.conversationStatus || 'active');
    }

    // Mark conversation as read and reload conversations to sync state
    await markConversationAsRead(conversationId);
    
    // Load conversation messages
    await loadConversationMessages(conversationId);
  }, [loadConversationMessages, markConversationAsRead, conversations]);

  // Handle conversation status change
  const handleConversationStatusChange = useCallback(async (conversationId, newStatus, statusData) => {
    // Update local state
    setSelectedConversationStatus(newStatus);

    // Update conversations list
    setConversations(prevConversations => {
      return prevConversations.map(conv => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            conversationStatus: newStatus, // Update the actual conversation status
            // Keep the assignment status (status field) unchanged
          };
        }
        return conv;
      });
    });

    // Add status change message to chat messages if this is the selected conversation
    if (selectedConversationId === conversationId && statusData) {
      const statusMessage = {
        id: `status_${conversationId}_${Date.now()}`,
        message_id: `status_${conversationId}_${Date.now()}`,
        conversation_id: conversationId,
        direction: 'internal',
        message_type: 'note',
        text: newStatus === 'active'
          ? `Conversation opened by ${statusData.opened_by || 'user'}`
          : `Conversation closed by ${statusData.closed_by || 'user'}`,
        content: {
          text: newStatus === 'active'
            ? `Conversation opened by ${statusData.opened_by || 'user'}`
            : `Conversation closed by ${statusData.closed_by || 'user'}`,
          note_type: 'status_change'
        },
        sent_at: statusData.opened_at || statusData.closed_at || new Date().toISOString(),
        status: 'sent'
      };

      setChatMessages(prevMessages => {
        // Check for duplicates
        const isDuplicate = prevMessages.some(msg => msg.id === statusMessage.id);
        if (isDuplicate) {
          return prevMessages;
        }

        // Add new message and sort by timestamp
        const newMessages = [...prevMessages, statusMessage];
        newMessages.sort((a, b) => {
          const timeA = new Date(a.sent_at).getTime();
          const timeB = new Date(b.sent_at).getTime();
          return timeA - timeB;
        });

        return newMessages;
      });
    }
  }, [selectedConversationId]);

  const handleFilterChange = useCallback((newFilterOptions) => {
    setFilterOptions(newFilterOptions);
  }, []);

  const getSelectedContact = useCallback(() => {
    const selectedConversation = selectedConversationId
      ? conversations.find(conv => conv.id === selectedConversationId)
      : null;

    if (!selectedConversation) return null;

    // Return contact object with all the necessary properties
    return {
      ...selectedConversation.contact,
      isContact: selectedConversation.isContact,
      contactId: selectedConversation.contactId,
      contactData: selectedConversation.contactData
    };
  }, [selectedConversationId, conversations]);

  const getCurrentAssignee = useCallback(() => {
    const selectedConversation = selectedConversationId
      ? conversations.find(conv => conv.id === selectedConversationId)
      : null;

    if (!selectedConversation || !selectedConversation.assignedAgent) return null;

    return selectedConversation.assignedAgent;
  }, [selectedConversationId, conversations]);

  const handleSendMessage = useCallback(async (messageText, messageType = 'text', mediaData = null) => {
    if (!selectedConversationId || !messageText.trim() || !accessToken) {
      console.error('Missing required data:', { selectedConversationId, messageText, accessToken });
      setError('Missing authentication or required data');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let endpoint;
      let payload;

      if (messageType === 'text') {
        endpoint = '/v2/whatsapp/internal/send-text-message';
        payload = {
          phone_id: phoneNumberId,
          waba_id: wabaId,
          recipient_number: getSelectedContact()?.phone,
          message: messageText,
        };
      } else if (messageType === 'template') {
        endpoint = '/v2/whatsapp/internal/send-template-message';
        payload = {
          phone_id: phoneNumberId,
          template_name: messageText,
          recipient_number: getSelectedContact()?.phone,
          language: 'en',
          components: mediaData?.components || []
        };
      }

      console.log('Sending message with payload:', payload, "token:", accessToken);
      const response = await postRequestAsync(endpoint, payload, accessToken.token);

      if (response.success) {
        await loadConversationMessages(selectedConversationId);
        await loadConversations();
      } else {
        setError(response.message || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  }, [selectedConversationId, accessToken, loadConversationMessages, loadConversations, getSelectedContact, wabaId, phoneNumberId]);

  const handleContactSave = useCallback(async (contactData, contactId) => {
    if (!accessToken?.token || !userRole || !wabaId) {
      throw new Error('Missing authentication or configuration');
    }

    try {
      let endpoint;
      let method;
      let payload;

      if (contactId) {
        // Update existing contact
        endpoint = `/v2/whatsapp/${userRole}/contacts/${contactId}`;
        method = 'PUT';
        payload = contactData;
      } else {
        // Create new contact
        endpoint = `/v2/whatsapp/${userRole}/contacts`;
        method = 'POST';
        payload = {
          ...contactData,
          waba_id: wabaId
        };
      }

      let response;
      if (method === 'POST') {
        response = await postRequestAsync(endpoint, payload, accessToken.token);
      } else {
        response = await putRequestAsync(endpoint, payload, accessToken.token);
      }

      if (response.success) {
        // Reload conversations to get updated contact information
        await loadConversations();
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to save contact');
      }
    } catch (err) {
      console.error('Error saving contact:', err);
      throw err;
    }
  }, [accessToken?.token, userRole, wabaId, loadConversations]);

  const handleAssignToMe = useCallback(async (conversationId) => {
    if (!accessToken?.token || !user?.id) {
      console.error('Missing authentication or user data');
      return;
    }

    try {
      const endpoint = userRole === 'manager'
        ? '/v2/whatsapp/manager/assign-conversation'
        : '/v2/whatsapp/agent/assign-conversation';

      const response = await postRequestAsync(endpoint, {
        conversation_id: conversationId,
        agent_user_id: user.id
      }, accessToken.token);

      if (response.success) {
        // Reload conversations to get updated assignment info
        await loadConversations();
        console.log('Conversation assigned to me successfully');
      } else {
        console.error('Failed to assign conversation:', response.message);
      }
    } catch (err) {
      console.error('Error assigning conversation to me:', err);
    }
  }, [accessToken?.token, user?.id, userRole, loadConversations]);

  const handleUnassign = useCallback(async (conversationId) => {
    if (!accessToken?.token) {
      console.error('Missing authentication');
      return;
    }

    try {
      const endpoint = userRole === 'manager'
        ? '/v2/whatsapp/manager/unassign-conversation'
        : '/v2/whatsapp/agent/unassign-conversation';

      const response = await postRequestAsync(endpoint, {
        conversation_id: conversationId
      }, accessToken.token);

      if (response.success) {
        // Reload conversations to get updated assignment info
        await loadConversations();
        console.log('Conversation unassigned successfully');
      } else {
        console.error('Failed to unassign conversation:', response.message);
      }
    } catch (err) {
      console.error('Error unassigning conversation:', err);
    }
  }, [accessToken?.token, userRole, loadConversations]);

  const handleAssignToAgent = useCallback(async (conversationId, agent) => {
    if (!accessToken?.token) {
      console.error('Missing authentication');
      return;
    }

    try {
      const endpoint = userRole === 'manager'
        ? '/v2/whatsapp/manager/assign-conversation'
        : '/v2/whatsapp/agent/assign-conversation';

      const response = await postRequestAsync(endpoint, {
        conversation_id: conversationId,
        agent_user_id: agent.id
      }, accessToken.token);

      if (response.success) {
        // Reload conversations to get updated assignment info
        await loadConversations();
        console.log('Conversation assigned to agent successfully');
      } else {
        console.error('Failed to assign conversation to agent:', response.message);
      }
    } catch (err) {
      console.error('Error assigning conversation to agent:', err);
    }
  }, [accessToken?.token, userRole, loadConversations]);

  // Determine user role based on Redux store user data
  const determineUserRole = useCallback(() => {
    // Try different possible paths for the role from Redux store and accessToken
    const userRole = user?.role || accessToken?.user?.role || accessToken?.role;

    if (!userRole) return;

    const role = userRole === 'manager' ? 'manager' : 'agent';
    setUserRole(role);
  }, [user, accessToken]);

  // Determine user role when component mounts
  useEffect(() => {
    if (user?.role || accessToken?.user?.role || accessToken?.role) {
      determineUserRole();
    }
  }, [determineUserRole]);

  useEffect(() => {
    if (isAuthenticated && userRole) {
      loadConversations();
    }
  }, [isAuthenticated, userRole, loadConversations]);

  useEffect(() => {
    if (filterOptions && Object.keys(filterOptions).length > 0) {
      loadConversations();
    }
  }, [filterOptions, loadConversations]);

  const selectedContact = getSelectedContact();
  const currentAssignee = getCurrentAssignee();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      alignSelf: 'stretch',
      padding: '0px 30px 10px 40px',
      height: 'calc(100vh - 160px)',
      maxHeight: 'calc(100vh - 160px)',
      overflow: 'hidden'
    }}>
      {/* Left Panel - Message List */}
      <WhatsAppLeftPanel
        messages={conversations}
        selectedMessageId={selectedConversationId}
        onMessageClick={handleConversationClick}
        onFilterChange={handleFilterChange}
        isLoading={isLoading}
        error={error}
        userRole={userRole}
        accessToken={accessToken}
        wabaId={wabaId}
      />

      {/* Right Panel - Chat Area */}
      <WhatsAppChatPanel
        selectedContact={selectedContact}
        messages={selectedConversationId ? chatMessages : []}
        onSendMessage={handleSendMessage}
        onContactSave={handleContactSave}
        currentAssignee={currentAssignee}
        onAssignToMe={handleAssignToMe}
        onUnassign={handleUnassign}
        onAssignToAgent={handleAssignToAgent}
        isLoading={isLoading}
        error={error}
        sseConnectionStatus={sseConnectionStatus}
        wabaId={wabaId}
        conversationStatus={selectedConversationStatus}
        onConversationStatusChange={handleConversationStatusChange}
      />
    </div>
  );
};

export default WhatsAppInboxTabContent;
